import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { PodcastEpisode, EpisodeStatus, PaginationMeta } from '@/types/episode.types';
import { useUserCompanies } from '@/contexts/UserCompaniesContext';
import { Search, RefreshCw, AlertCircle, Plus, Loader2, User, List, LayoutGrid, Filter } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { EpisodeListingSkeleton } from '@/components/EpisodeListingSkeleton';
import { Pagination } from '@/components/ui/pagination';
import { PodcastEpisodeService } from '@/services/PodcastEpisodeService';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { StatusBadge } from '@/components/StatusBadge';
// MetricChips temporarily unused but may be re-enabled
// import { MetricChips } from '@/components/MetricChips';
import { FilterChips, FilterCategory } from '@/components/FilterChips';
import { formatRelativeDate } from '@/utils/dateFormat';
import { customToast } from '@/components/ui/custom-toast';
import { connectSocket, onPodcastMetricsUpdate, PodcastMetricsEvent } from '@/services/socket';
import { useIsMobile } from '@/hooks/use-mobile';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { MobileEpisodeListing } from '@/components/mobile';
import { Mic } from 'lucide-react';
import { FeatureBanner } from '@/components/FeatureBanner';
import { useHelpContext } from '@/components/help-widget/HelpContext';
import { useBreadcrumb } from '@/contexts/BreadcrumbContext';

// Map filter categories to their corresponding statuses
const FILTER_CATEGORY_STATUSES: Record<FilterCategory, EpisodeStatus[]> = {
  draft: [
    EpisodeStatus.DRAFT,
    EpisodeStatus.GENERATING_SCRIPTS,
    EpisodeStatus.GENERATING_SCRIPT,
    EpisodeStatus.SCRIPTS_APPROVED,
    EpisodeStatus.GENERATING_AUDIO,
    EpisodeStatus.MERGING,
    EpisodeStatus.PUBLISHING,
  ],
  needsReview: [
    EpisodeStatus.SCRIPTS_PENDING_REVIEW,
    EpisodeStatus.SCRIPT_READY,
    EpisodeStatus.AUDIO_PENDING_REVIEW,
    EpisodeStatus.AUDIO_READY,
  ],
  ready: [
    EpisodeStatus.MERGED,
    EpisodeStatus.AUDIO_APPROVED,
  ],
  published: [
    EpisodeStatus.PUBLISHED,
  ],
  failed: [
    EpisodeStatus.FAILED,
    EpisodeStatus.SCRIPT_FAILED,
    EpisodeStatus.AUDIO_FAILED,
    EpisodeStatus.PUBLISHING_FAILED,
  ],
  archived: [
    EpisodeStatus.ARCHIVED,
  ],
};

// Debounce delay for search (ms)
const SEARCH_DEBOUNCE_MS = 300;
const ITEMS_PER_PAGE = 10;

type ViewMode = 'table' | 'cards';

export const EpisodeListing: React.FC = () => {
  const isMobile = useIsMobile();
  const breakpoint = useBreakpoint();
  const navigate = useNavigate();
  const { setContext } = useHelpContext();
  const { userCompanies, getCompanyColor } = useUserCompanies();
  const { setItems: setBreadcrumbItems } = useBreadcrumb();

  // Set breadcrumb items — runs before paint so bar persists across navigations
  useLayoutEffect(() => {
    setBreadcrumbItems([
      { label: 'Episode Composer', href: '../dashboard', icon: <Mic style={{ width: 14, height: 14, strokeWidth: 1.75 }} /> },
      { label: 'Episodes' },
    ]);
    return () => setBreadcrumbItems([]);
  }, [setBreadcrumbItems]);

  // Set help context for episodes listing
  useEffect(() => {
    setContext('episode-composer:episodes');
    return () => setContext(null);
  }, [setContext]);
  const [episodes, setEpisodes] = useState<PodcastEpisode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState(''); // Input value (updates immediately)
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const requestCounterRef = useRef(0); // Prevents stale API responses from displaying
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem('dashboard-view-mode');
    return (saved === 'cards' || saved === 'table') ? saved : 'table';
  });

  // Persist view mode to localStorage
  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    localStorage.setItem('dashboard-view-mode', mode);
  };

  // Filter chips - draft and main workflow selected by default, archived not selected
  const [selectedFilters, setSelectedFilters] = useState<Set<FilterCategory>>(
    () => new Set<FilterCategory>(['draft', 'needsReview', 'ready', 'published', 'failed'])
  );

  // Server-side pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationMeta, setPaginationMeta] = useState<PaginationMeta>({
    page: 1,
    limit: ITEMS_PER_PAGE,
    total: 0,
    totalPages: 0,
  });

  const [creatingEpisode, setCreatingEpisode] = useState(false);

  // Pre-computed metrics from API (O(1) read instead of O(n) client-side computation)
  // Metrics state temporarily unused but kept for future re-enablement
  const [, setMetrics] = useState<{
    needsReview: number;
    inProgress: number;
    readyToPublish: number;
    published: number;
    failed: number;
  }>({ needsReview: 0, inProgress: 0, readyToPublish: 0, published: 0, failed: 0 });
  const [, setMetricsLoading] = useState(true);

  // Determine if user has multiple companies
  const isMultiCompany = userCompanies.length > 1;

  // Get company name by ID for multi-company badge
  const getCompanyName = useCallback((companyId: string): string => {
    return userCompanies.find(c => c.id === companyId)?.name || '';
  }, [userCompanies]);

  // Get company initials (first letter of first two words)
  const getCompanyInitials = useCallback((companyId: string): string => {
    const name = userCompanies.find(c => c.id === companyId)?.name || '';
    if (!name) return '?';
    // Split into words and take first letter of first two words
    const words = name.trim().split(/\s+/);
    if (words.length === 1) {
      // Single word: use first 2 characters
      return name.substring(0, 2).toUpperCase();
    }
    // Multiple words: first letter of first word + first letter of second word
    return (words[0][0] + words[1][0]).toUpperCase();
  }, [userCompanies]);

  // Get user initials from display name
  const getUserInitials = (name: string | undefined): string => {
    if (!name) return '?';
    const words = name.trim().split(/\s+/);
    if (words.length === 1) {
      return name.substring(0, 2).toUpperCase();
    }
    return (words[0][0] + words[1][0]).toUpperCase();
  };

  // Generate consistent color for user avatars based on name
  const getUserAvatarColor = (name: string | undefined): string => {
    if (!name) return 'var(--pm-text-faint)';
    const colors = [
      '#4A90E2', // Blue
      '#7B68EE', // Medium Slate Blue
      '#48C774', // Green
      '#F39C12', // Orange
      '#E74C3C', // Red
      '#1ABC9C', // Turquoise
      '#9B59B6', // Purple
      '#34495E', // Dark Blue Gray
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  // Load metrics on mount
  useEffect(() => {
    loadMetrics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Subscribe to real-time metrics updates via WebSocket
  useEffect(() => {
    let cleanup: (() => void) | undefined;

    const setupWebSocket = async () => {
      try {
        await connectSocket();
        cleanup = onPodcastMetricsUpdate((event: PodcastMetricsEvent) => {
          setMetrics(event.metrics);
        });
      } catch (error) {
        console.error('[Dashboard] Error setting up WebSocket for metrics:', error);
      }
    };

    setupWebSocket();

    return () => {
      if (cleanup) cleanup();
    };
  }, []);

  // Load pre-computed metrics from API (O(1) operation)
  const loadMetrics = async () => {
    setMetricsLoading(true);
    try {
      const fetchedMetrics = await PodcastEpisodeService.getMetrics();
      setMetrics(fetchedMetrics);
    } catch (err) {
      console.error('Error loading metrics:', err);
      setMetrics({ needsReview: 0, inProgress: 0, readyToPublish: 0, published: 0, failed: 0 });
    } finally {
      setMetricsLoading(false);
    }
  };

  // Toggle a filter category
  const toggleFilter = (category: FilterCategory) => {
    setSelectedFilters(prev => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };

  // Convert selected filter categories to flat array of EpisodeStatus values
  const getSelectedStatuses = useCallback((): EpisodeStatus[] => {
    const statuses: EpisodeStatus[] = [];
    selectedFilters.forEach((category) => {
      statuses.push(...FILTER_CATEGORY_STATUSES[category]);
    });
    return statuses;
  }, [selectedFilters]);

  // Load episodes with server-side pagination, search, and status filtering
  const loadEpisodes = useCallback(async (
    page: number = 1,
    search: string = '',
    statuses?: EpisodeStatus[],
  ) => {
    // If no statuses provided, show empty state without calling API
    if (statuses && statuses.length === 0) {
      setEpisodes([]);
      setPaginationMeta({ page: 1, limit: ITEMS_PER_PAGE, total: 0, totalPages: 0 });
      setCurrentPage(1);
      setLoading(false);
      return;
    }

    const requestId = ++requestCounterRef.current;
    setLoading(true);
    setError(null);
    try {
      // Extract company IDs for multi-company query
      const companyIds = userCompanies.length > 0
        ? userCompanies.map(c => c.id)
        : undefined;

      const result = await PodcastEpisodeService.getEpisodesPaginated({
        page,
        limit: ITEMS_PER_PAGE,
        search: search || undefined,
        orderBy: 'updatedAt',
        order: 'desc',
        companyIds,
        status: statuses && statuses.length > 0 ? statuses : undefined,
      });

      // Only apply results if this is still the latest request
      if (requestId !== requestCounterRef.current) return;

      setEpisodes(result.episodes);
      setPaginationMeta(result.meta);
      setCurrentPage(result.meta.page);
    } catch (err) {
      if (requestId !== requestCounterRef.current) return;
      console.error('Error loading episodes:', err);
      setError('Failed to load episodes. Please try again.');
    } finally {
      if (requestId === requestCounterRef.current) {
        setLoading(false);
      }
    }
  }, [userCompanies]);

  // Debounced search handler
  const handleSearchInputChange = (value: string) => {
    setSearchInput(value);

    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout for debounced search
    searchTimeoutRef.current = setTimeout(() => {
      setSearchTerm(value);
    }, SEARCH_DEBOUNCE_MS);
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    loadEpisodes(newPage, searchTerm, getSelectedStatuses());
  };

  // Handle manual search button click
  const handleSearch = () => {
    setSearchTerm(searchInput);
  };

  // Load episodes on mount and when filters or search change
  useEffect(() => {
    setCurrentPage(1);
    loadEpisodes(1, searchTerm, getSelectedStatuses());
  }, [loadEpisodes, searchTerm, selectedFilters, getSelectedStatuses]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleRowClick = (episodeId: string) => {
    navigate(`../episodes/${episodeId}`);
  };

  const handleCreateEpisode = async (companyId?: string) => {
    try {
      setCreatingEpisode(true);
      const { id } = await PodcastEpisodeService.createDraftEpisode(undefined, companyId);
      customToast.success('Episode created');
      navigate(`../episodes/${id}`);
    } catch (err: any) {
      customToast.error('Failed to create episode', {
        description: err.message || 'Please try again',
      });
      setCreatingEpisode(false);
    }
  };

  // Render mobile view for phone screens
  if (isMobile) {
    return <MobileEpisodeListing />;
  }

  // Show skeleton while loading
  if (loading) {
    return <EpisodeListingSkeleton />;
  }

  const hPad = breakpoint === 'desktop' ? '32px' : '16px';

  return (
    <>
      {/* Section 1: Header Area - contained card */}
      <div className="max-w-content" style={{ paddingLeft: hPad, paddingRight: hPad, paddingTop: '24px', paddingBottom: '24px' }}>
      <div className="bg-podcast rounded-lg border border-border shadow-sm" style={{ padding: '20px 24px 24px 24px' }}>
        <div>
          {/* Page Title */}
          <h1 className="text-h1 mb-5">Podcast Episodes</h1>

          {/* Search and Filters Bar */}
          <div className="flex justify-between items-center gap-2 flex-wrap">
            <div className="flex gap-2">
              <Input
                placeholder="Search episodes..."
                value={searchInput}
                onChange={(e) => handleSearchInputChange(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className={breakpoint === 'desktop' ? 'w-64' : 'w-40'}
              />
              <Button onClick={handleSearch} size="icon" variant="secondary">
                <Search className="h-4 w-4" />
              </Button>
            </div>

            {/* Filter Toggle, View Toggle, and Refresh - Right aligned */}
            <div className="flex gap-4 items-center">
              {/* Filter Toggle with dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" title="Toggle filters">
                    <Filter className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="p-3">
                  <FilterChips
                    selectedFilters={selectedFilters}
                    onToggle={toggleFilter}
                  />
                </DropdownMenuContent>
              </DropdownMenu>

              {/* View Toggle */}
              <div className="flex items-center border border-[var(--pm-border-default)] rounded-md overflow-hidden">
                <button
                  onClick={() => handleViewModeChange('table')}
                  className={`p-2 transition-colors ${
                    viewMode === 'table'
                      ? 'bg-[var(--pm-surface-4)] text-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-[var(--pm-surface-2)]'
                  }`}
                  title="Table view"
                >
                  <List className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleViewModeChange('cards')}
                  className={`p-2 transition-colors ${
                    viewMode === 'cards'
                      ? 'bg-[var(--pm-surface-4)] text-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-[var(--pm-surface-2)]'
                  }`}
                  title="Card view"
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
              </div>

              <Button onClick={() => loadEpisodes(currentPage, searchTerm, getSelectedStatuses())} variant="ghost" size="icon" title="Refresh episodes">
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>

              {isMultiCompany ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" disabled={creatingEpisode} data-testid="add-episode-button">
                      {creatingEpisode ? (
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <Plus className="h-4 w-4 mr-1" />
                      )}
                      {creatingEpisode ? 'Creating...' : 'New'}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {userCompanies.map((company) => (
                      <DropdownMenuItem
                        key={company.id}
                        onClick={() => handleCreateEpisode(company.id)}
                      >
                        <div
                          className="w-3 h-3 rounded-full mr-2"
                          style={{ backgroundColor: getCompanyColor(company.id) || '#4A90E2' }}
                        />
                        {company.name}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button
                  size="sm"
                  onClick={() => handleCreateEpisode()}
                  disabled={creatingEpisode}
                  data-testid="add-episode-button"
                >
                  {creatingEpisode ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4 mr-1" />
                  )}
                  {creatingEpisode ? 'Creating...' : 'New'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
      </div>

      {/* Section 3: Main Content - constrained width */}
      <div className="max-w-content pb-8" style={{ paddingLeft: hPad, paddingRight: hPad, paddingTop: '24px' }}>
        {/* Feature Spotlight Banner */}
        <FeatureBanner contextTrigger="episode-composer:episode-listing" />

        {/* Error Message */}
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Episodes List */}
        <div>
        {episodes.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">No episodes found</p>
          </div>
        ) : (
          <>
            {viewMode === 'table' ? (
              // Table view - Card container
              <div
                className="flex flex-col overflow-hidden"
                style={{
                  backgroundColor: 'var(--pm-bg-card)',
                  border: '1px solid var(--pm-border-default)',
                  borderRadius: '12px',
                  maxHeight: 'calc(100vh - 280px)',
                }}
              >
                {/* Table Header */}
                <div
                  className="flex items-center gap-4 px-4 py-3 text-xs text-muted-foreground uppercase tracking-wide min-w-0"
                  style={{
                    backgroundColor: 'var(--pm-cyan-bg)',
                    borderBottom: '1px solid var(--pm-border-default)',
                  }}
                >
                  <div className="w-10 shrink-0"></div>
                  <div className="flex-1 min-w-0">Episode</div>
                  <div className="w-10 text-center shrink-0">Created</div>
                  <div className="w-10 text-center shrink-0">Edited</div>
                  <div className="w-16 text-center shrink-0">Clips</div>
                  <div className="w-32 text-right shrink-0">Status</div>
                </div>

                {/* Table Rows */}
                <div className="flex-1 overflow-y-auto min-h-0">
                {episodes.map((episode, index) => (
                  <div
                    key={episode.id}
                    className="flex items-center gap-4 px-4 py-4 cursor-pointer transition-colors duration-200 min-w-0"
                    style={{
                      borderBottom: index < episodes.length - 1 ? '1px solid var(--pm-cyan-bg-strong)' : 'none',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--pm-cyan-bg-strong)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    onClick={() => handleRowClick(episode.id)}
                  >
                    {/* Company Avatar */}
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 text-sm font-medium text-white"
                      style={{
                        backgroundColor: `${getCompanyColor(episode.companyId) || '#4A90E2'}80`,
                      }}
                      title={getCompanyName(episode.companyId)}
                    >
                      {getCompanyInitials(episode.companyId)}
                    </div>

                    {/* Title and Date */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium truncate" title={episode.title}>
                          {episode.title}
                        </span>
                        {/* Multi-company badge */}
                        {/* {isMultiCompany && episode.companyId && (
                          <span
                            className="flex-shrink-0 text-xs px-1.5 py-0.5 rounded"
                            style={{ border: '1px solid rgba(0, 212, 255, 0.3)', color: 'var(--pm-text-muted)' }}
                          >
                            {getCompanyName(episode.companyId)}
                          </span>
                        )} */}
                      </div>
                      <div className="text-sm text-muted-foreground" title={format(new Date(episode.updatedAt), 'MMM d, yyyy h:mm a')}>
                        {formatRelativeDate(episode.updatedAt)}
                      </div>
                    </div>

                    {/* Created By Avatar */}
                    <div className="shrink-0 w-10 flex justify-center">
                      {episode.createdByAvatarUrl ? (
                        <img
                          src={episode.createdByAvatarUrl}
                          alt=""
                          className="w-8 h-8 rounded-full object-cover"
                          title={`Created by ${episode.createdByName || 'Unknown'}`}
                        />
                      ) : (
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium text-white"
                          style={{ backgroundColor: getUserAvatarColor(episode.createdByName) }}
                          title={`Created by ${episode.createdByName || 'Unknown'}`}
                        >
                          {getUserInitials(episode.createdByName)}
                        </div>
                      )}
                    </div>

                    {/* Edited By Avatar */}
                    <div className="shrink-0 w-10 flex justify-center">
                      {episode.updatedByAvatarUrl ? (
                        <img
                          src={episode.updatedByAvatarUrl}
                          alt=""
                          className="w-8 h-8 rounded-full object-cover"
                          title={`Last edited by ${episode.updatedByName || 'Unknown'}`}
                        />
                      ) : (
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium text-white"
                          style={{ backgroundColor: getUserAvatarColor(episode.updatedByName) }}
                          title={`Last edited by ${episode.updatedByName || 'Unknown'}`}
                        >
                          {getUserInitials(episode.updatedByName)}
                        </div>
                      )}
                    </div>

                    {/* Clips Count */}
                    <div className="text-sm text-muted-foreground shrink-0 w-16 text-center">
                      {episode.clipCount ?? 0} clips
                    </div>

                    {/* Status Badge */}
                    <div className="shrink-0 w-32 text-right">
                      <StatusBadge status={episode.status} scheduledPublishDate={episode.scheduledPublishDate} />
                    </div>
                  </div>
                ))}
                </div>

                {/* Pagination Footer */}
                <div
                  className="px-4 py-3 text-sm text-muted-foreground text-right"
                  style={{
                    borderTop: '1px solid var(--pm-border-default)',
                  }}
                >
                  <Pagination
                    currentPage={paginationMeta.page}
                    totalPages={paginationMeta.totalPages}
                    totalItems={paginationMeta.total}
                    onPageChange={handlePageChange}
                    className=""
                  />
                </div>
              </div>
            ) : (
              // Card view
              <div className="flex flex-col overflow-hidden" style={{ maxHeight: 'calc(100vh - 280px)' }}>
              <div className="flex-1 overflow-y-auto min-h-0">
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                {episodes.map((episode) => (
                  <div
                    key={episode.id}
                    className="flex flex-col p-4 rounded-xl cursor-pointer transition-all duration-200"
                    style={{
                      backgroundColor: 'var(--pm-bg-card)',
                      border: '1px solid var(--pm-border-default)',
                      minHeight: '220px',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--pm-cyan-bg-strong)';
                      e.currentTarget.style.borderColor = 'var(--pm-border-emphasis)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--pm-bg-card)';
                      e.currentTarget.style.borderColor = 'var(--pm-border-default)';
                    }}
                    onClick={() => handleRowClick(episode.id)}
                  >
                    {/* Title - max 2 lines */}
                    <div className="flex items-start gap-2 flex-wrap">
                      <h3
                        className="font-medium line-clamp-2 leading-snug"
                        style={{ color: 'var(--pm-text-primary)' }}
                        title={episode.title}
                      >
                        {episode.title}
                      </h3>
                      {/* Multi-company badge */}
                      {isMultiCompany && episode.companyId && (
                        <span
                          className="flex-shrink-0 text-xs px-1.5 py-0.5 rounded whitespace-nowrap"
                          style={{ border: '1px solid var(--pm-chip-bg)', color: 'var(--pm-text-muted)' }}
                        >
                          {getCompanyName(episode.companyId)}
                        </span>
                      )}
                    </div>

                    {/* Description - max 3 lines */}
                    <p
                      className="text-sm line-clamp-3 mt-2"
                      style={{ color: episode.description ? 'var(--pm-text-muted)' : 'var(--pm-text-faint)' }}
                    >
                      {episode.description || 'No description'}
                    </p>

                    {/* Spacer to push status and footer to bottom */}
                    <div className="flex-1" />

                    {/* Status Row - fixed position */}
                    <div className="flex items-center justify-between mt-4">
                      <StatusBadge status={episode.status} scheduledPublishDate={episode.scheduledPublishDate} />
                      <span className="text-sm" style={{ color: 'var(--pm-text-muted)' }}>
                        {episode.clipCount ?? 0} clips
                      </span>
                    </div>

                    {/* Footer Row - always at bottom */}
                    <div
                      className="flex items-center justify-between mt-3 text-xs"
                      style={{ color: 'var(--pm-text-faint)' }}
                    >
                      <span title={format(new Date(episode.updatedAt), 'MMM d, yyyy h:mm a')}>
                        Updated {formatRelativeDate(episode.updatedAt)}
                      </span>
                      {episode.createdByName && (
                        <div className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          <span className="truncate max-w-[100px]">{episode.createdByName}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              </div>
              <Pagination
                currentPage={paginationMeta.page}
                totalPages={paginationMeta.totalPages}
                totalItems={paginationMeta.total}
                onPageChange={handlePageChange}
                className="mt-4 pt-2"
              />
              </div>
            )}
          </>
        )}
        </div>
      </div>

    </>
  );
};
